import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { useCompare } from "../App";
import { Menu, X, Search, User, Heart, GitCompare, ChevronDown, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { compareList } = useCompare();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const cities = [
    { name: "Bengaluru", slug: "bengaluru" },
    { name: "Mumbai", slug: "mumbai" },
    { name: "Hyderabad", slug: "hyderabad" },
    { name: "Chennai", slug: "chennai" },
    { name: "Pune", slug: "pune" },
    { name: "Delhi-NCR", slug: "delhi-ncr" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage
          ? "glass border-b border-stone-200/50"
          : "bg-transparent"
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className={`font-display text-xl md:text-2xl tracking-tight ${isScrolled || !isHomePage ? "text-emerald-900" : "text-white"}`}>
              RERA <span className="text-amber-600">Approved</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Cities Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  isScrolled || !isHomePage ? "text-stone-700 hover:text-emerald-900" : "text-white/90 hover:text-white"
                }`}
                data-testid="cities-dropdown-trigger"
              >
                Cities <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {cities.map((city) => (
                  <DropdownMenuItem key={city.slug} asChild>
                    <Link to={`/city/${city.slug}`} className="cursor-pointer">
                      {city.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Projects Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  isScrolled || !isHomePage ? "text-stone-700 hover:text-emerald-900" : "text-white/90 hover:text-white"
                }`}
                data-testid="projects-dropdown-trigger"
              >
                Projects <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/search?category=ready_to_move" className="cursor-pointer">
                    Ready to Move
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/search?category=under_construction" className="cursor-pointer">
                    Under Construction
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/search?category=new_launch" className="cursor-pointer">
                    New Launch
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/search?category=upcoming" className="cursor-pointer">
                    Upcoming Projects
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/emi-calculator"
              className={`text-sm font-medium transition-colors ${
                isScrolled || !isHomePage ? "text-stone-700 hover:text-emerald-900" : "text-white/90 hover:text-white"
              }`}
              data-testid="emi-calculator-link"
            >
              EMI Calculator
            </Link>

            <Link
              to="/blog"
              className={`text-sm font-medium transition-colors ${
                isScrolled || !isHomePage ? "text-stone-700 hover:text-emerald-900" : "text-white/90 hover:text-white"
              }`}
              data-testid="blog-link"
            >
              Blog
            </Link>

            <Link
              to="/compare"
              className={`flex items-center gap-1 text-sm font-medium transition-colors relative ${
                isScrolled || !isHomePage ? "text-stone-700 hover:text-emerald-900" : "text-white/90 hover:text-white"
              }`}
              data-testid="compare-link"
            >
              <GitCompare className="w-4 h-4" strokeWidth={1.5} />
              Compare
              {compareList.length > 0 && (
                <span className="absolute -top-2 -right-3 bg-amber-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {compareList.length}
                </span>
              )}
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Search Icon */}
            <button
              onClick={() => navigate("/search")}
              className={`p-2 rounded-full transition-colors ${
                isScrolled || !isHomePage ? "hover:bg-stone-100 text-stone-700" : "hover:bg-white/10 text-white"
              }`}
              data-testid="search-btn"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={`flex items-center gap-2 p-2 rounded-full transition-colors ${
                    isScrolled || !isHomePage ? "hover:bg-stone-100 text-stone-700" : "hover:bg-white/10 text-white"
                  }`}
                  data-testid="user-menu-trigger"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-900 text-white flex items-center justify-center text-sm font-medium">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-stone-100">
                    <p className="font-medium text-stone-900">{user.name}</p>
                    <p className="text-sm text-stone-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer flex items-center gap-2">
                      <User className="w-4 h-4" strokeWidth={1.5} />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard?tab=saved" className="cursor-pointer flex items-center gap-2">
                      <Heart className="w-4 h-4" strokeWidth={1.5} />
                      Saved Properties
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className={`hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-none transition-all ${
                  isScrolled || !isHomePage
                    ? "bg-emerald-900 text-white hover:bg-emerald-800"
                    : "bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/20"
                }`}
                data-testid="login-btn"
              >
                <User className="w-4 h-4" strokeWidth={1.5} />
                Login
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 rounded-full transition-colors ${
                isScrolled || !isHomePage ? "hover:bg-stone-100 text-stone-700" : "hover:bg-white/10 text-white"
              }`}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden glass border-t border-stone-200/50 py-4">
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-widest text-stone-500 px-2">Cities</p>
              <div className="grid grid-cols-2 gap-2">
                {cities.map((city) => (
                  <Link
                    key={city.slug}
                    to={`/city/${city.slug}`}
                    className="text-stone-700 hover:text-emerald-900 px-2 py-1 text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {city.name}
                  </Link>
                ))}
              </div>
              <div className="border-t border-stone-200 pt-4 space-y-2">
                <p className="text-xs uppercase tracking-widest text-stone-500 px-2 mt-2">Projects</p>
                <Link
                  to="/search?category=ready_to_move"
                  className="block text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Ready to Move
                </Link>
                <Link
                  to="/search?category=under_construction"
                  className="block text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Under Construction
                </Link>
                <Link
                  to="/search?category=new_launch"
                  className="block text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  New Launch
                </Link>
                <Link
                  to="/search?category=upcoming"
                  className="block text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Upcoming Projects
                </Link>
                <Link
                  to="/emi-calculator"
                  className="block text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  EMI Calculator
                </Link>
                <Link
                  to="/blog"
                  className="block text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  to="/compare"
                  className="flex items-center gap-2 text-stone-700 hover:text-emerald-900 px-2 py-2 text-sm font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <GitCompare className="w-4 h-4" /> Compare ({compareList.length})
                </Link>
              </div>
              {!user && (
                <div className="border-t border-stone-200 pt-4">
                  <Link
                    to="/login"
                    className="block w-full text-center bg-emerald-900 text-white py-3 text-sm font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login / Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
